// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { makeRecordFixture } from "../../../utils/zod";
import { FixtureMapping } from "../../utils/types";
import {
  UsTnReclassification2026ReferralRecord,
  usTnReclassification2026Schema,
} from "./schema";

export const usTnReclassification2026PolicyFixtures = {
  fullyEligible: makeRecordFixture(usTnReclassification2026Schema, {
    stateCode: "US_TN",
    externalId: "RES003",
    isEligible: true,
    isAlmostEligible: false,
    eligibleCriteria: {
      custodyLevelIsNotMax: null,
      usTnAtLeast12MonthsSinceLatestAssessment: {
        initialAssessmentDate: "2024-12-28",
        AnnualReclassificationDate: "2025-01-01",
        AnnualReclassificationDecisionDate: "2025-01-01",
      },
    },
    ineligibleCriteria: {},
    formInformation: {
      sixMonthsAgo: "2024-06-01",
      twelveMonthsAgo: "2023-12-01",
      eighteenMonthsAgo: "2023-06-01",
      thirtySixMonthsAgo: "2021-12-01",
      sixtyMonthsAgo: "2019-12-01",
      activeRecommendations: [],
      hasIncompatibles: false,
      incompatibleArray: [],
      statusAtHearingSeg: "GEN",
      q2CurrentOffenseDate: "2022-06-21",
      q3Score: -3,
      q4Score: 2,
      q5Score: -1,
      q6Score: 0,
      q7Score: 1,
      q1Notes: {
        listPriorNonTdocConvictions60Months: [
          {
            description: "some offense",
            imposedDate: "2022-12-01",
          },
        ],
        listPriorViolentTdocConvictions60Months: [
          {
            description: "some other offense",
            imposedDate: "2021-08-22",
          },
        ],
      },
      q2Notes: ["a third offense"],
      q3Notes: [
        {
          numIncidents: 1,
          incidentTimePeriod: "0-60Months",
          incidents: [
            {
              incidentClass: "Q",
              incidentDate: "2023-01-08",
              incidentTypeCode: "ABC",
            },
          ],
        },
      ],
      q6Notes: {
        age: 37,
      },
      q7Notes: [
        {
          programCode: "ABC",
          programType: "Letter stitching",
          completionDate: "2022-02-02",
        },
        {
          programCode: "XYZ",
          programType: "Xylophone Ensemble",
          completionDate: "2024-04-04",
        },
      ],
      isServingLife: false,
      trusteeHas10YearsOrLessRemaining: true,
      trusteeNoAssaultiveDisciplinaryWithSeriousInjury: true,
      trusteeNoEscapeFromLowTrusteePast5Years: true,
      trusteeNoEscapeFromMediumCloseMaxPast10Years: true,
      trusteeNotConvictedOfFirstDegreeMurder: true,
      trusteeNotScoredHighForViolence: true,
      trusteeNotServingForSexualOffense: false,
      trusteeNotConvictedOfViolentOffenseOr12MonthsInCustody: true,
      trusteeNoViolentFelonyConvictionPast5YearsIncarceration: true,
      trusteeNoAssaultiveDisciplinaryWithSeriousInjuryLast5Years: true,
      trusteeNoAssaultiveDisciplinaryWithSeriousInjuryMoreThan5YearsAgo: true,
      trusteeNotOnClinicalAlertStatus: true,
      trusteeNotOnLevelOfCare3Or4Or5: true,
      trusteeHas7YearsOrLessRemaining: true,
    },
  }),
  ineligible: makeRecordFixture(usTnReclassification2026Schema, {
    stateCode: "US_TN",
    externalId: "RES003",
    isEligible: false,
    isAlmostEligible: false,
    eligibleCriteria: {},
    ineligibleCriteria: {
      custodyLevelIsNotMax: {
        custodyLevel: "Maximum",
      },
      usTnAtLeast12MonthsSinceLatestAssessment: {
        initialAssessmentDate: "2024-12-28",
        AnnualReclassificationDate: "2025-01-01",
        AnnualReclassificationDecisionDate: "2025-01-01",
      },
    },
    formInformation: {
      sixMonthsAgo: "2024-06-01",
      twelveMonthsAgo: "2023-12-01",
      eighteenMonthsAgo: "2023-06-01",
      thirtySixMonthsAgo: "2021-12-01",
      sixtyMonthsAgo: "2019-12-01",
      hasIncompatibles: true,
      activeRecommendations: [{ Recommendation: "do this thing" }],
      incompatibleArray: [
        { incompatibleOffenderId: "123", incompatibleType: "magentism" },
        { incompatibleOffenderId: "456", incompatibleType: "psychic" },
      ],
      statusAtHearingSeg: "GEN",
      q3Score: -3,
      q4Score: 2,
      q5Score: -1,
      q6Score: 0,
      q7Score: 1,
      q1Notes: {
        listPriorNonTdocConvictions60Months: [
          {
            description: "some offense",
            imposedDate: "2022-12-01",
          },
        ],
        listPriorViolentTdocConvictions60Months: [
          {
            description: "some other offense",
            imposedDate: "2021-08-22",
          },
        ],
      },
      q2Notes: ["a third offense", "a fourth offense"],
      q3Notes: [
        {
          numIncidents: 2,
          incidentTimePeriod: "0-6Months",
          incidents: [
            {
              incidentClass: "Q",
              incidentDate: "2023-01-08",
              incidentTypeCode: "ABC",
            },
            {
              incidentClass: "R",
              incidentDate: "2022-02-11",
              incidentTypeCode: "XYZ",
            },
          ],
        },
        {
          numIncidents: 2,
          incidentTimePeriod: "24-48Months",
          incidents: [
            {
              incidentClass: "Q",
              incidentDate: "2021-01-08",
              incidentTypeCode: "RFV",
            },
            {
              incidentClass: "R",
              incidentDate: "2020-02-11",
              incidentTypeCode: "IKM",
            },
          ],
        },
      ],
      q4Notes: [
        {
          numIncidents: 1,
          incidentTimePeriod: "6-12Months",
          incidents: [
            {
              incidentClass: "Q",
              incidentDate: "2023-01-08",
              incidentTypeCode: "DEF",
            },
          ],
        },
      ],
      q5Notes: [
        {
          numIncidents: 1,
          incidentTimePeriod: "12-24Months",
          incidents: [
            {
              incidentClass: "Q",
              incidentDate: "2023-01-08",
              incidentTypeCode: "HIJ",
            },
          ],
        },
      ],
      q6Notes: {
        age: 26,
      },
      isServingLife: false,
      trusteeHas10YearsOrLessRemaining: true,
      trusteeNoAssaultiveDisciplinaryWithSeriousInjury: true,
      trusteeNoEscapeFromLowTrusteePast5Years: true,
      trusteeNoEscapeFromMediumCloseMaxPast10Years: true,
      trusteeNotConvictedOfFirstDegreeMurder: true,
      trusteeNotScoredHighForViolence: true,
      trusteeNotServingForSexualOffense: false,
      trusteeNotConvictedOfViolentOffenseOr12MonthsInCustody: true,
      trusteeNoViolentFelonyConvictionPast5YearsIncarceration: true,
      trusteeNoAssaultiveDisciplinaryWithSeriousInjuryLast5Years: true,
      trusteeNoAssaultiveDisciplinaryWithSeriousInjuryMoreThan5YearsAgo: true,
      trusteeNotOnClinicalAlertStatus: true,
      trusteeNotOnLevelOfCare3Or4Or5: true,
      trusteeHas7YearsOrLessRemaining: true,
    },
  }),
} satisfies FixtureMapping<UsTnReclassification2026ReferralRecord>;
